import { useEffect, useState } from 'react';
import styles from './SystemSettingsTab.module.css';
import { useSettingsStore } from '../../../store/settingsStore';
import { useAuthStore } from '../../../store/authStore';
import { settingsApi, chatHistoryApi } from '../../../lib/apiClient';
import { toast } from '../../../lib/toast';
import ToggleSwitch from '../../../ui/ToggleSwitch/ToggleSwitch';

export default function SystemSettingsTab() {
  const { resolvedSettings, resolvedLoading, loadResolvedSettings } = useSettingsStore();
  const { user } = useAuthStore();
  const isSuperAdmin = user?.role?.id === 1;
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [historyActionLoading, setHistoryActionLoading] = useState(false);

  useEffect(() => {
    loadResolvedSettings();
  }, []);


  const chatAiSetting = resolvedSettings.find(s => s.key === 'chat_ai_agent_enabled');
  const chatIsEnabled = useSettingsStore(s => s.isChatAiAgentEnabled())

  const chatHistoryDisabledSetting = resolvedSettings.find(s => s.key === 'chat_history_disabled');
  const isChatHistoryDisabled = chatHistoryDisabledSetting?.value === 'true' || chatHistoryDisabledSetting?.value === true;

  const isChatDebugUsageEnabled = useSettingsStore(s => s.isChatDebugUsageEnabled());

  const handleToggle = async () => {
    if (!chatAiSetting) return;
    try {
      await settingsApi.setValue('chat_ai_agent_enabled' as any, { value: String(!chatIsEnabled) });
      await loadResolvedSettings();
    } catch (e: any) {
      console.error('Failed to toggle Chat AI Agent:', e);
    }
  };

  const handleHistoryToggle = async () => {
    if (!isChatHistoryDisabled) {
      setShowDisableConfirm(true);
    } else {
      setHistoryActionLoading(true);
      try {
        await settingsApi.setValue('chat_history_disabled' as any, { value: 'false' });
        await loadResolvedSettings();
        toast.success('Chat history enabled');
      } catch (e: any) {
        toast.error('Failed to enable chat history');
      } finally {
        setHistoryActionLoading(false);
      }
    }
  };

  const handleConfirmDisable = async () => {
    setShowDisableConfirm(false);
    setHistoryActionLoading(true);
    try {
      await chatHistoryApi.disableHistory();
      await loadResolvedSettings();
      toast.success('Chat history disabled and all records deleted');
    } catch (e: any) {
      toast.error('Failed to disable chat history');
    } finally {
      setHistoryActionLoading(false);
    }
  };

  const handleTokenUsageToggle = async () => {
    if (!chatAiSetting) return;
    try {
      await settingsApi.setValue('chat_debug_usage_enabled' as any, { value: String(!isChatDebugUsageEnabled) });
      await loadResolvedSettings();
    } catch (e: any) {
      console.error('Failed to toggle Chat Token Usage:', e);
    }
  };

  if (resolvedLoading && resolvedSettings.length === 0) {
    return <div className={styles.loading}>Loading system settings...</div>;
  }

  return (
    <div className={styles.tab}>
      <div className={styles.header}>
        <h2>System</h2>
        <p>Control system-wide features and capabilities.</p>
      </div>

      <div className={styles.settingRow}>
        <div className={styles.settingInfo}>
          <h3 className={styles.settingLabel}>Chat AI Agent</h3>
          <p className={styles.settingDescription}>
            When enabled, Admin users can access the Chat AI Agent menu including
            Analytics, AI Agents (active only), and Chat History.
          </p>
          <p className={styles.settingNote}>
            This setting only affects Admin-level users. Super Admin users always have full access.
          </p>
        </div>
        <div className={styles.settingControl}>
          <ToggleSwitch
            checked={chatIsEnabled}
            onChange={handleToggle}
            disabled={!chatAiSetting || resolvedLoading}
          />
          <div className={`${styles.statusLabel} ${chatIsEnabled ? styles.statusOn : styles.statusOff}`}>
            {chatIsEnabled ? 'Active' : 'Inactive'}
          </div>
        </div>
      </div>

      {isSuperAdmin && (
        <>
          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <h3 className={styles.settingLabel}>Disable Chat History</h3>
              <p className={styles.settingDescription}>
                When enabled, all chat history is permanently deleted and new conversations will not be saved.
              </p>
              <p className={styles.settingNote}>
                This setting is visible and operable by Super Admins only.
              </p>
            </div>
            <div className={styles.settingControl}>
              <ToggleSwitch
                checked={isChatHistoryDisabled}
                onChange={handleHistoryToggle}
                disabled={historyActionLoading}
              />
              <div className={`${styles.statusLabel} ${isChatHistoryDisabled ? styles.statusDanger : styles.statusOff}`}>
                {isChatHistoryDisabled ? 'Disabled' : 'Active'}
              </div>
            </div>
          </div>

          {showDisableConfirm && (
            <div className={styles.confirmOverlay}>
              <div className={styles.confirmModal}>
                <h4 className={styles.confirmTitle}>Disable Chat History?</h4>
                <p className={styles.confirmBody}>
                  This will <strong>permanently delete all chat history</strong> for all users.
                  New conversations will not be saved. This action cannot be undone.
                </p>
                <div className={styles.confirmActions}>
                  <button
                    className={styles.confirmCancel}
                    onClick={() => setShowDisableConfirm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className={styles.confirmDelete}
                    onClick={handleConfirmDisable}
                  >
                    Yes, Delete All &amp; Disable
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <h3 className={styles.settingLabel}>Show Chat Debug Usage</h3>
              <p className={styles.settingDescription}>
                When enabled, the cost of tokens used and debug information in each chat will be displayed.
              </p>
              <p className={styles.settingNote}>
                This setting is visible and operable by Super Admins only.
              </p>
            </div>
            <div className={styles.settingControl}>
              <ToggleSwitch
                checked={isChatDebugUsageEnabled}
                onChange={handleTokenUsageToggle}
                disabled={!chatAiSetting || resolvedLoading}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
